import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACES_COST_TOTAL_COSTS_QUERY_KEY = 'namespaces-cost-total-costs';

const getNamespacesCostTotalCostsQueryKey = ({
  startTime,
  endTime,
  clusterId,
  useListingPrices,
  pageLimit,
  includeIdleCosts,
}: Partial<Props> = {}) =>
  [
    NAMESPACES_COST_TOTAL_COSTS_QUERY_KEY,
    startTime,
    endTime,
    clusterId,
    useListingPrices,
    pageLimit,
    includeIdleCosts ?? false,
  ] as const;

type Props = {
  startTime: string;
  endTime: string;
  clusterId: string;
  useListingPrices: boolean;
  pageLimit?: number;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getNamespacesCostTotalCostsQueryOptions = ({
  startTime,
  endTime,
  clusterId,
  useListingPrices,
  pageLimit = 5,
  enabled = true,
  includeIdleCosts = false,
}: Props) =>
  infiniteQueryOptions({
    queryKey: getNamespacesCostTotalCostsQueryKey({
      startTime,
      endTime,
      clusterId,
      useListingPrices,
      pageLimit,
      includeIdleCosts,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getNamespacesTotalCosts({
        startTime,
        endTime,
        clusterId,
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        filter: {},
        useListingPrices,
        includeIdleResourceCosts: includeIdleCosts,
      });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useNamespacesCostTotalCostsQuery = (props: Props) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getNamespacesCostTotalCostsQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
    noDataReason: data?.pages[0].noDataReason ?? '',
  };
};
